import React, { useState, useRef, useEffect } from 'react';
import { Image as ImageIcon, Download, Loader, Sparkles, Settings, Boxes, Upload, Edit3, Wand2, Trash2, Plus, Video, BookOpen, Palette, Zap, Lightbulb, Film, RefreshCw, Bookmark, BookmarkCheck, FileText, X, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getConfig } from '../../../config/production';
import VideoGeneration from './video/VideoGeneration';
import { InfoModal } from '../../common/InfoModal';
import SavedPromptsModal from '../../common/SavedPromptsModal';
import SavePromptDialog from '../../common/SavePromptDialog';
import { apiGet, apiPost, apiPostJson } from '../../../utils/api';
import { useDragAndDrop } from '../../../hooks';
import DragDropOverlay from '../../common/DragDropOverlay';
import { modelServicePost } from '../../../utils/modelService';
import HistorySidebar from './creativeStudio/HistorySidebar';
import PromptRefiner from './creativeStudio/PromptRefiner';
import VariantGrid from './creativeStudio/VariantGrid';
import { useQueryClient } from '@tanstack/react-query';
import RotatingStatusIndicator from '../../common/RotatingStatusIndicator';
import { IMAGE_GEN_STATEMENTS } from '../../common/statusStatements';

const API_URL_BASE = getConfig('API_URL') || 'https://api.jpro.app/api/v1';
const cleanUrl = API_URL_BASE.endsWith('/') ? API_URL_BASE.slice(0, -1) : API_URL_BASE;
const API_URL = cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`;

function Imagen() {
    const { theme } = useTheme();
    const { token } = useAuth();
    const fileInputRef = useRef(null);
    const promptInputRef = useRef(null);
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // File upload handler for drag and drop
    const handleFilesDropped = (files) => {
        const fileArray = Array.from(files);
        fileArray.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const base64Data = e.target.result.split(',')[1];
                    const newImage = {
                        id: crypto.randomUUID(),
                        name: file.name,
                        content_type: file.type,
                        content: base64Data,
                        preview: e.target.result
                    };
                    setUploadedImages(prev => [...prev, newImage]);
                };
                reader.readAsDataURL(file);
            }
        });
    };

    // Drag and drop functionality
    const { isDragging, dragHandlers } = useDragAndDrop(handleFilesDropped);
    
    // Tab management - check URL params for initial tab
    const urlTab = searchParams.get('tab');
    const validTabs = ['image', 'video'];
    const [activeTab, setActiveTab] = useState(validTabs.includes(urlTab) ? urlTab : 'image'); // Default to image

    // Common states
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [savingContent, setSavingContent] = useState('');
    const [showPromptsModal, setShowPromptsModal] = useState(false);

    // Creative Studio settings — per-session UI prefs
    const [creativeStudioSettings, setCreativeStudioSettings] = useState({
        aspectRatio: '1:1',
        resolution: '2K',  // 1K, 2K, or 4K
        showThinkingProcess: false,    // Show intermediate images (user preference)
        variantCount: 1,               // 1–4 variants per generation
        promptRefinerEnabled: true,    // Show/hide the JPRO Intelligence ✨ button
    });

    // Thought signature for multi-turn context
    const [thoughtSignature, setThoughtSignature] = useState(null);

    // Reference images for multi-image consistency (up to 14)
    // Legacy bucket — kept for the existing upload path. New slot-based refs
    // live in slotImages below and are merged into reference_images on send.
    const [referenceImages, setReferenceImages] = useState([]);

    // Context files (PDF / Word / Excel) — text extracted server-side.
    // Images use uploadedImages (unified via the "+" button).
    const [contextFiles, setContextFiles] = useState([]);

    // Transient variants from the last /generate-image call. Only the picked
    // one persists to history (creative_generations). Cleared on next generation.
    const [variants, setVariants] = useState([]);
    const [pickedVariantId, setPickedVariantId] = useState(null);

    // Lineage tracking for "Continue from here" — set when restoring from history.
    const [parentGenerationId, setParentGenerationId] = useState(null);

    // History sidebar collapse — defaults to collapsed; persisted so the user's
    // preference sticks once they open it. The localStorage key only flips the
    // default when explicitly set to '0' (expanded).
    const [historyCollapsed, setHistoryCollapsed] = useState(() => {
        try { return localStorage.getItem('creative_history_collapsed') !== '0'; }
        catch { return true; }
    });
    const toggleHistory = () => {
        setHistoryCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem('creative_history_collapsed', next ? '1' : '0'); } catch {}
            return next;
        });
    };

    // Most recently saved row id, so we don't double-save the same variant.
    const lastSavedVariantRef = useRef(null);

    // React Query client for invalidating the history list after saves
    const queryClient = useQueryClient();
    
    // AI Studio states (chat-like interface)
    const [conversationHistory, setConversationHistory] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [studioChatId, setStudioChatId] = useState(null);
    const [studioSessionId, setStudioSessionId] = useState(null);
    const [showGuide, setShowGuide] = useState(false);

    // Load Creative Studio conversation on component mount or URL changing
    useEffect(() => {
        if (token && activeTab === 'image') {
            loadStudioConversation();
        }
    }, [token, activeTab, searchParams]);

    // Auto-focus prompt input when Studio image tab is active
    useEffect(() => {
        if (activeTab !== 'image') return;
        const t = window.setTimeout(() => {
            promptInputRef.current?.focus();
        }, 50);
        return () => window.clearTimeout(t);
    }, [activeTab]);

    // Unified upload handler — images go to uploadedImages (used as references
    // by the backend), docs go to contextFiles (server-side text extraction).
    // Single "+" button accepts every supported type.
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const dataUrl = e.target.result;
                const base64Data = String(dataUrl).split(',')[1] || '';

                if (file.type.startsWith('image/')) {
                    setUploadedImages(prev => [...prev, {
                        id: crypto.randomUUID(),
                        name: file.name,
                        content_type: file.type,
                        content: base64Data,
                        preview: dataUrl,
                    }]);
                } else {
                    // Treat as context doc (PDF / Word / Excel). Server extracts text.
                    setContextFiles(prev => [...prev, {
                        id: crypto.randomUUID(),
                        name: file.name,
                        content_type: file.type || 'application/octet-stream',
                        content: base64Data,
                    }]);
                }
            };
            reader.readAsDataURL(file);
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeUploadedImage = (imageId) => {
        setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    };

    // Save AI Studio conversation to Rails backend
    const saveStudioConversation = async (userMessage, aiMessage) => {
        if (!token) return;
        
        let currentChatId = studioChatId;
        
        try {
            // Create new chat if needed
            if (!currentChatId) {
                const sessionId = studioSessionId || `ai_studio_${Date.now()}`;
                setStudioSessionId(sessionId);
                
                const chatPayload = {
                    chat: {
                        title: `🎨 AI Studio: ${userMessage.content.slice(0, 30)}...`,
                        session_id: sessionId,
                        metadata: JSON.stringify({
                            source: 'ai_studio',
                            interface_type: 'image_generation',
                            created_from: 'ai_studio_chat'
                        })
                    }
                };

                // Using apiPost with automatic token refresh
                const chatResponse = await apiPost('/chats', chatPayload);
                
                if (chatResponse.ok) {
                    const chatData = await chatResponse.json();
                    currentChatId = chatData.chat.id;
                    setStudioChatId(currentChatId);
                    localStorage.setItem('ai_studio_chat_id', currentChatId.toString());
                } else {
                    console.error('Failed to create AI Studio chat');
                    return;
                }
            }
            
            // Save user message. Strip base64 from attached_files before persisting
            // — only keep id/name/mime so chips can render on resume with a "re-upload to
            // use again" affordance. Full content lives in client memory for the session
            // only; cross-session re-use would need Active Storage (deferred follow-up).
            const attachedFilesMeta = (userMessage.attached_files || []).map(f => ({
                id: f.id,
                name: f.name,
                content_type: f.content_type,
                // content intentionally omitted — never stored in Rails DB
            }));
            const userMessagePayload = {
                message: {
                    content: userMessage.content,
                    is_user: true,
                    metadata: JSON.stringify({
                        source: 'ai_studio',
                        mode: userMessage.mode || 'image_generation',
                        uploaded_images: userMessage.images ? userMessage.images.length : 0,
                        attached_files: attachedFilesMeta,
                    })
                }
            };

            // Using apiPost with automatic token refresh
            await apiPost(`/chats/${currentChatId}/messages`, userMessagePayload);
            
            // Save AI message
            const aiMessagePayload = {
                message: {
                    content: aiMessage.content,
                    is_user: false,
                    metadata: JSON.stringify({
                        source: 'ai_studio',
                        model: 'gemini-2.0-flash-preview-image-generation',
                        provider: 'Google',
                        mode: aiMessage.mode,
                        images_generated: aiMessage.images ? aiMessage.images.length : 0,
                        image_data: aiMessage.images || null
                    })
                }
            };

            // Using apiPost with automatic token refresh
            await apiPost(`/chats/${currentChatId}/messages`, aiMessagePayload);
            
        } catch (error) {
            console.error('Error saving AI Studio conversation:', error);
        }
    };

    // Load existing AI Studio conversation
    const loadStudioConversation = async () => {
        if (!token) return;
        
        // Check URL params for specific chat ID first
        const urlChatId = searchParams.get('chat_id');
        const targetChatId = urlChatId || localStorage.getItem('ai_studio_chat_id');
        
        if (!targetChatId) return;

        // If we're switching to a different chat, wipe transient state first so
        // nothing from the previous chat (thought_signature, variants, attached
        // files, etc.) leaks into the new one.
        if (studioChatId && String(studioChatId) !== String(targetChatId)) {
            resetChatTransientState();
        }

        try {
            // Using apiGet with automatic token refresh
            const response = await apiGet(`/chats/${targetChatId}`);

            if (response.ok) {
                const chatData = await response.json();
                const messages = chatData.chat.messages || [];
                
                // Verify this is actually a Creative Studio chat
                if (chatData.chat.session_id && !chatData.chat.session_id.startsWith('ai_studio_')) {
                    console.warn('Chat is not a Creative Studio chat, ignoring');
                    return;
                }
                
                const loadedHistory = messages.map(msg => {
                    let metadata = {};
                    try {
                        metadata = JSON.parse(msg.metadata || '{}');
                    } catch (e) {
                        console.warn('Failed to parse message metadata:', e);
                    }
                    
                    return {
                        id: msg.id,
                        type: msg.is_user ? 'user' : 'ai',
                        content: msg.content,
                        images: metadata.image_data || null,
                        mode: metadata.mode || null,
                        // Restore attached_files metadata; content is null on resume
                        // because we don't persist base64 to the DB. UI shows chips
                        // as disabled with a "Re-upload to use again" tooltip.
                        attached_files: Array.isArray(metadata.attached_files) ? metadata.attached_files : null,
                        timestamp: msg.created_at
                    };
                });
                
                setConversationHistory(loadedHistory);
                setStudioChatId(parseInt(targetChatId));
                setStudioSessionId(chatData.chat.session_id);
                
                // Update localStorage if loaded from URL
                if (urlChatId) {
                    localStorage.setItem('ai_studio_chat_id', targetChatId);
                }
            } else {
                // Chat not found, clear stored ID
                localStorage.removeItem('ai_studio_chat_id');
            }
        } catch (error) {
            console.error('Error loading AI Studio conversation:', error);
            localStorage.removeItem('ai_studio_chat_id');
        }
    };

    const downloadImage = (imageData) => {
        const link = document.createElement('a');
        link.href = `data:${imageData.mime_type};base64,${imageData.image_base64}`;

        // Handle different prompt formats
        let filename;
        if (imageData.prompt_used) {
            filename = `ai_studio_${imageData.prompt_used.slice(0, 20)}_${Date.now()}.png`;
        } else if (imageData.prompt) {
            filename = `creative_studio_${imageData.prompt.slice(0, 20)}_${Date.now()}.png`;
        } else {
            filename = `generated_image_${Date.now()}.png`;
        }

        link.download = filename.replace(/[^a-z0-9_\-\.]/gi, '_');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Low-level save: persists ONE variant payload to /api/v1/creative_generations.
    // No state guards — caller is responsible for not double-saving.
    const saveVariantToHistory = async (target, promptText) => {
        if (!target?.image_base64) return null;
        try {
            const payload = {
                creative_generation: {
                    prompt: promptText || '(no prompt)',
                    original_prompt: target._originalPrompt || null,
                    refinement_notes: target._refinementNotes || null,
                    mode: 'creative_studio',
                    model_used: target._modelUsed || 'gemini-3-pro-image-preview',
                    aspect_ratio: creativeStudioSettings.aspectRatio,
                    resolution: creativeStudioSettings.resolution,
                    image_base64: target.image_base64,
                    mime_type: target.mime_type || 'image/png',
                    thought_signature: target.thought_signature || null,
                    parent_id: parentGenerationId || null,
                    reference_labels: (uploadedImages || []).map(img => img.name || 'reference'),
                    context_file_names: (contextFiles || []).map(f => f.name),
                    metadata: target._metadata || {},
                },
            };
            const saved = await apiPostJson('/creative_generations', payload);
            queryClient.invalidateQueries({ queryKey: ['creative-generations', 'index'] });
            // The freshly-saved row becomes the parent of the next generation in this chain.
            if (saved?.generation?.id) setParentGenerationId(saved.generation.id);
            return saved;
        } catch (e) {
            console.error('Failed to save variant to history:', e);
            return null;
        }
    };

    // Save a specific image to history. Called from the bookmark buttons on AI
    // messages and variant tiles — never automatic. Returns the saved row so
    // the caller can mark the image as saved in state.
    const handleSaveImage = async ({ image, promptText, source }) => {
        // source can be { kind: 'message', messageId } or { kind: 'variant', variantId }
        const target = {
            image_base64: image.image_base64,
            mime_type: image.mime_type || 'image/png',
            thought_signature: image.thought_signature || thoughtSignature,
            _modelUsed: image._modelUsed || 'gemini-3-pro-image-preview',
            _metadata: image._metadata || {},
            _originalPrompt: image._originalPrompt || null,
            _refinementNotes: image._refinementNotes || null,
        };
        const saved = await saveVariantToHistory(target, promptText || prompt || '(no prompt)');
        if (!saved) return null;

        // Mark as saved so the UI can show "Saved" instead of "Save"
        if (source?.kind === 'message') {
            setConversationHistory((prev) => prev.map((msg) => {
                if (msg.id !== source.messageId) return msg;
                return {
                    ...msg,
                    images: (msg.images || []).map((img) =>
                        img.image_base64 === image.image_base64 ? { ...img, _savedId: saved.generation?.id || true } : img
                    ),
                };
            }));
        } else if (source?.kind === 'variant') {
            setVariants((prev) => prev.map((v) =>
                (v.id ?? -1) === source.variantId ? { ...v, _savedId: saved.generation?.id || true } : v
            ));
        }
        return saved;
    };

    // "Continue from here" handler invoked by HistorySidebar. No auto-save —
    // user is expected to have already bookmarked anything they wanted to keep.
    const handleContinueFromHistory = async (generation) => {
        setVariants([]);
        setPickedVariantId(null);
        lastSavedVariantRef.current = null;

        // Restore the saved generation as the "previous_image" context
        setParentGenerationId(generation.id);
        setThoughtSignature(generation.thought_signature || null);
        setPrompt(generation.prompt || '');

        // Treat it as if it's the most recent ai message so previous_image lookup picks it up
        const ghostMessage = {
            id: `restored_${generation.id}`,
            type: 'ai',
            content: 'Restored from history — continue editing.',
            images: [{
                image_base64: generation.image_base64,
                mime_type: generation.mime_type,
                prompt_used: generation.prompt,
            }],
            timestamp: generation.created_at,
            restored: true,
        };
        setConversationHistory((prev) => [...prev, ghostMessage]);
    };

    // Sync thoughtSignature to the picked variant's signature whenever the
    // pick changes. This is what makes "pick variant 3 then iterate" actually
    // continue from variant 3 (not variant 1) in the next generation.
    useEffect(() => {
        if (pickedVariantId == null || !Array.isArray(variants) || variants.length === 0) return;
        const v = variants.find((x, i) => (x.id ?? i) === pickedVariantId);
        if (v?.thought_signature) {
            setThoughtSignature(v.thought_signature);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickedVariantId]);

    // AI Studio chat handler — no auto-save. Variants are transient until the
    // user explicitly bookmarks them via the save button on the image.
    const handleStudioChat = async () => {
        if (!prompt.trim() && uploadedImages.length === 0) {
            setError('Please enter a message or upload images');
            return;
        }

        // Capture the previously-picked variant BEFORE clearing transient state.
        // This drives `previous_image` and `thought_signature` for the next turn
        // so the model continues from the variant the user actually picked,
        // not just the first one in the last response.
        const prevPicked = (variants && variants.length > 0)
            ? (variants.find((v, i) => (v.id ?? i) === pickedVariantId) || variants[0])
            : null;
        const prevSignature = prevPicked?.thought_signature || thoughtSignature;

        // Clear any previous transient variants. Anything not explicitly saved
        // by the user is gone.
        setVariants([]);
        setPickedVariantId(null);
        lastSavedVariantRef.current = null;

        // Snapshot files for THIS API call. After send, contextFiles state clears
        // so the next message starts fresh. We store the full base64 on the
        // message bubble so the user can click a past chip to "re-use" the file
        // in a future turn (opt-in, no auto-include).
        const contextFilesSnapshot = [...(contextFiles || [])];
        const filesForThisMessage = contextFilesSnapshot.map(f => ({
            id: f.id,
            name: f.name,
            content_type: f.content_type,
            content: f.content,  // kept for opt-in re-use from chat history
        }));

        // Add user message to conversation
        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: prompt.trim(),
            images: uploadedImages.length > 0 ? [...uploadedImages] : null,
            attached_files: filesForThisMessage.length > 0 ? filesForThisMessage : null,
            timestamp: new Date().toISOString()
        };

        setConversationHistory(prev => [...prev, userMessage]);
        setPrompt('');
        setContextFiles([]);  // Files belong to the message that was just sent; clear input for next message
        setIsGenerating(true);
        setError('');
        
        // Add loading message
        const loadingMessageId = Date.now() + 0.5;
        setConversationHistory(prev => [...prev, {
            id: loadingMessageId,
            type: 'ai',
            content: 'Creating your image...',
            isLoading: true,
            timestamp: new Date().toISOString()
        }]);

        try {
            // Determine mode based on whether images are uploaded
            const mode = uploadedImages.length > 0 ? 'image_editing' : 'text_to_image';

            // Unified references: every image attached via the "+" button is a
            // reference. The backend labels them generically; no semantic slots.
            const refs = (referenceImages || []).map(img => ({
                content: img.content,
                content_type: img.content_type,
                label: img.label || `reference_${img.id}`,
            }));

            const requestBody = {
                prompt: userMessage.content,
                mode: mode,
                input_images: uploadedImages.map(img => ({
                    content: img.content,
                    content_type: img.content_type
                })),
                aspect_ratio: creativeStudioSettings.aspectRatio,
                resolution: creativeStudioSettings.resolution,
                // Text rendering is now auto-detected by backend based on prompt content
                thought_signature: prevSignature,  // Prefer the picked variant's signature
                enable_grounding: true,  // Always enabled - AI decides when to use real-time data
                show_thinking_process: creativeStudioSettings.showThinkingProcess,
                variant_count: Math.max(1, Math.min(4, creativeStudioSettings.variantCount || 1)),
                reference_images: refs,
                // Send context files for THIS message only. After send, state is cleared
                // so they're not re-sent on subsequent turns. Backend keeps context via
                // thought_signature; re-attach to inject fresh docs.
                context_files: contextFilesSnapshot.map(f => ({
                    name: f.name,
                    mime_type: f.content_type,
                    content: f.content,
                })),
            };

            // Only include previous image context when NOT uploading new images
            // (to avoid confusing the model with multiple images).
            // Priority: the picked variant from the prior turn > last AI message's first image.
            //
            // prompt_used on previous_image describes the PRIOR prompt that produced
            // the prior image — never the new prompt the user is currently typing.
            // The backend turns this into "Previous image context: this image was
            // created with the prompt 'X'", and mislabeling it gives the model
            // inconsistent context.
            if (uploadedImages.length === 0) {
                if (prevPicked?.image_base64) {
                    requestBody.previous_image = {
                        image_base64: prevPicked.image_base64,
                        mime_type: prevPicked.mime_type,
                        prompt_used: prevPicked._promptUsed || undefined,
                    };
                } else {
                    const lastAiMessage = conversationHistory
                        .filter(msg => msg.type === 'ai' && msg.images)
                        .slice(-1)[0];
                    if (lastAiMessage?.images?.[0]) {
                        // lastAiMessage.images[0] already has the correct prompt_used from
                        // when the backend produced it.
                        requestBody.previous_image = lastAiMessage.images[0];
                    }
                }
            }

            const response = await modelServicePost('/generate-image', requestBody);

            const result = await response.json();

            if (!response.ok) {
                // Extract detailed error message from backend
                const errorMessage = result.detail || result.error || response.statusText;
                throw new Error(errorMessage);
            }

            // Build a unified variants array regardless of which response shape the
            // backend used. Multi-variant: result.variants[]. Single: result.images[]
            // or top-level image_base64. This normalization lets the save flow treat
            // every generation as a 1-to-N variants set.
            //
            // _promptUsed is the prompt that ACTUALLY PRODUCED this variant — needed
            // so a later "previous_image" reference correctly tells the model what
            // the prior image is, not what the user is now asking for.
            const promptForThisGeneration = userMessage.content;
            let stamped = [];
            if (Array.isArray(result.variants) && result.variants.length > 0) {
                stamped = result.variants.map((v, i) => ({
                    id: i,
                    image_base64: v.image_base64,
                    mime_type: v.mime_type,
                    thought_signature: v.thought_signature,
                    _modelUsed: result.model_used,
                    _promptUsed: promptForThisGeneration,
                    _metadata: { grounding: result.grounding_metadata || null },
                }));
            } else if (Array.isArray(result.images) && result.images.length > 0) {
                stamped = result.images.map((img, i) => ({
                    id: i,
                    image_base64: img.image_base64,
                    mime_type: img.mime_type,
                    thought_signature: result.thought_signature,
                    _modelUsed: result.model_used,
                    _promptUsed: img.prompt_used || promptForThisGeneration,
                    _metadata: { grounding: result.grounding_metadata || null },
                }));
            } else if (result.image_base64) {
                stamped = [{
                    id: 0,
                    image_base64: result.image_base64,
                    mime_type: result.mime_type || 'image/png',
                    thought_signature: result.thought_signature,
                    _modelUsed: result.model_used,
                    _promptUsed: promptForThisGeneration,
                    _metadata: { grounding: result.grounding_metadata || null },
                }];
            }

            setVariants(stamped);
            setPickedVariantId(0);
            lastSavedVariantRef.current = null;
            // No auto-save. Images live in the chat / variant grid; user uses
            // the bookmark button on each image to keep it in history.

            // Handle multiple images or single image response (legacy field)
            const images = result.images || [result];

            // Save thought signature for next turn context (legacy single-variant path)
            if (result.thought_signature) {
                setThoughtSignature(result.thought_signature);
                logger.info('Saved thought signature for multi-turn context');
            }

            // Store images as documents first
            const processedImages = await Promise.all(images.map(async (imageData) => {
                try {
                    // Using apiPost with automatic token refresh
                    const storeResponse = await apiPost('/documents/store_generated_image', {
                        image_data: imageData.image_base64,
                        mime_type: imageData.mime_type,
                        prompt: `AI Studio: ${userMessage.content}`
                    });

                    let documentUrl = null;
                    if (storeResponse.ok) {
                        const storeResult = await storeResponse.json();
                        documentUrl = storeResult.document?.download_url;
                    }

                    return {
                        ...imageData,
                        document_url: documentUrl
                    };
                } catch (error) {
                    console.error('Failed to store image:', error);
                    return imageData; // Return original if storage fails
                }
            }));
            
            // Create AI response message
            const aiMessage = {
                id: Date.now() + 1,
                type: 'ai',
                content: result.text_content || 'Here are the images I created for you:',
                images: processedImages,
                thinking_images: result.thinking_images || null,  // Intermediate images
                grounding_metadata: result.grounding_metadata || null,  // Search grounding info
                mode: mode,
                timestamp: new Date().toISOString(),
                // Stash the prompt that produced this so the per-image bookmark button
                // can save with the right context even if conversation state shifts.
                userPromptSnapshot: userMessage.content,
            };

            // Remove loading message and add AI response
            setConversationHistory(prev => 
                prev.filter(msg => msg.id !== loadingMessageId).concat(aiMessage)
            );
            
            // Save conversation to Rails backend
            await saveStudioConversation(userMessage, aiMessage);
            
            // Clear uploaded images after use
            setUploadedImages([]);

        } catch (err) {
            console.error('AI Studio chat error:', err);
            setError(err.message);
            
            // Remove loading message and add error message
            const errorMessage = {
                id: Date.now() + 1,
                type: 'error',
                content: `Sorry, I encountered an error: ${err.message}`,
                timestamp: new Date().toISOString()
            };
            setConversationHistory(prev => 
                prev.filter(msg => msg.id !== loadingMessageId).concat(errorMessage)
            );
        } finally {
            setIsGenerating(false);
        }
    };

    // Clear AI Studio conversation
    // Wipe ALL chat-scoped transient state. Anything that could carry signal
    // from one chat to another belongs here. Called when starting a new chat
    // AND at the start of loading a different chat.
    const resetChatTransientState = () => {
        setThoughtSignature(null);
        setVariants([]);
        setPickedVariantId(null);
        setParentGenerationId(null);
        setContextFiles([]);
        setUploadedImages([]);
        setReferenceImages([]);
        setPrompt('');
        setError('');
        lastSavedVariantRef.current = null;
    };

    const clearStudioChat = () => {
        setConversationHistory([]);
        setStudioChatId(null);
        setStudioSessionId(null);
        localStorage.removeItem('ai_studio_chat_id');
        resetChatTransientState();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleStudioChat();
        }
    };

    // Handle paste events for images
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const files = [];

        // Iterate through clipboard items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Handle image files
            if (item.kind === 'file' && item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }

        // If we found image files, process them
        if (files.length > 0) {
            e.preventDefault(); // Prevent default paste behavior
            files.forEach(file => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const base64Data = event.target.result.split(',')[1];
                    const newImage = {
                        id: crypto.randomUUID(),
                        name: file.name || 'pasted-image.png',
                        content_type: file.type,
                        content: base64Data,
                        preview: event.target.result
                    };
                    setUploadedImages(prev => [...prev, newImage]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    return (
        <div className={`h-full overflow-y-auto p-3 sm:p-4 lg:p-6 max-w-screen-2xl mx-auto ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="space-y-4 sm:space-y-6">
                {/* Header */}
                        
                {/* Header with Integrated Navigation */}
                <div className="mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                        {/* Left side - Tab Navigation */}
                        <div className={`relative flex rounded-lg p-0.5 w-full sm:w-auto ${theme === 'dark' ? 'bg-gray-800/50 backdrop-blur-sm border border-gray-700/50' : 'bg-white/80 backdrop-blur-sm border border-gray-200/50 shadow-sm'}`}>
                            {/* Background slider */}
                            {/* <div
                                className={`absolute top-0.5 bottom-0.5 transition-all duration-300 ease-out rounded-md shadow-md ${
                                    activeTab === 'image'
                                    ? 'left-0.5 w-1/2 bg-gradient-to-r from-purple-500 to-blue-600'
                                    : 'left-1/2 w-1/2 bg-gradient-to-r from-green-500 to-green-600'
                                }`}
                            /> */}

                            {/* <button
                                onClick={() => setActiveTab('image')}
                                className={`relative z-10 flex-1 flex items-center justify-center space-x-1 sm:space-x-1.5 py-2 px-2 sm:px-3 rounded-md font-rasa text-xs sm:text-sm font-medium transition-all duration-300 ${
                                    activeTab === 'image'
                                    ? 'text-white'
                                    : theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                <ImageIcon className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${activeTab === 'image' ? 'scale-110' : ''}`} />
                                <span className="font-medium">Image</span>
                            </button> */}

                            {/* <button
                                onClick={() => setActiveTab('video')}
                                className={`relative z-10 flex-1 flex items-center justify-center space-x-1 sm:space-x-1.5 py-2 px-2 sm:px-3 rounded-md font-rasa text-xs sm:text-sm font-medium transition-all duration-300 ${
                                    activeTab === 'video'
                                    ? 'text-white'
                                    : theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                <Video className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${activeTab === 'video' ? 'scale-110' : ''}`} />
                                <span className="font-medium">Video</span>
                            </button> */}
                        </div>

                        {/* Right side - Settings and Actions */}
                        <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
                            {/* Image Tab Actions */}
                            {activeTab === 'image' && (
                                <>
                                    {/* Guide Button */}
                                    <button
                                        onClick={() => setShowGuide(true)}
                                        className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 shadow-sm transition-all text-xs"
                                    >
                                        <BookOpen className="w-3 h-3" />
                                        Guide
                                    </button>

                                    <div className="relative">
                                        <button
                                            onClick={() => setShowSettings(!showSettings)}
                                            className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-lg transition-all duration-200 ${
                                                showSettings
                                                ? theme === 'dark'
                                                    ? 'bg-blue-600 text-white shadow-md'
                                                    : 'bg-blue-600 text-white shadow-md'
                                                : theme === 'dark'
                                                    ? 'bg-gray-700/70 hover:bg-gray-600 text-gray-300 hover:text-white border border-gray-600/50'
                                                    : 'bg-gray-100/70 hover:bg-gray-200 text-gray-600 hover:text-gray-800 border border-gray-200/50'
                                            }`}
                                            title={showSettings ? "Hide settings" : "Show settings"}
                                        >
                                            <Settings className={`w-4 h-4 transition-transform duration-200 ${showSettings ? 'rotate-90' : ''}`} />
                                            <span className="text-sm font-medium font-rasa">Settings</span>
                                        </button>

                                        {/* Settings Dropdown */}
                                        {showSettings && (
                                            <div className={`absolute right-0 top-full mt-2 w-72 p-4 rounded-lg shadow-lg border z-50 ${
                                                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                            }`}>
                                                <h4 className="text-sm font-semibold font-unna mb-3">Creative Studio Settings</h4>

                                                <div className="space-y-3">
                                                    {/* Aspect Ratio */}
                                                    <div>
                                                        <label className="block text-sm font-medium font-rasa mb-2">Aspect Ratio</label>
                                                        <select
                                                            value={creativeStudioSettings.aspectRatio}
                                                            onChange={(e) => setCreativeStudioSettings({...creativeStudioSettings, aspectRatio: e.target.value})}
                                                            className={`w-full p-2 text-sm rounded border font-rasa ${
                                                                theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                        >
                                                            <option value="1:1">Square (1:1)</option>
                                                            <option value="2:3">Portrait (2:3)</option>
                                                            <option value="3:2">Landscape (3:2)</option>
                                                            <option value="3:4">Portrait (3:4)</option>
                                                            <option value="4:3">Landscape (4:3)</option>
                                                            <option value="4:5">Portrait (4:5)</option>
                                                            <option value="5:4">Landscape (5:4)</option>
                                                            <option value="9:16">Tall (9:16)</option>
                                                            <option value="16:9">Wide (16:9)</option>
                                                            <option value="21:9">Ultra Wide (21:9)</option>
                                                        </select>
                                                    </div>

                                                    {/* Resolution */}
                                                    <div>
                                                        <label className="block text-sm font-medium font-rasa mb-2">Resolution</label>
                                                        <select
                                                            value={creativeStudioSettings.resolution}
                                                            onChange={(e) => setCreativeStudioSettings({...creativeStudioSettings, resolution: e.target.value})}
                                                            className={`w-full p-2 text-sm rounded border font-rasa ${
                                                                theme === 'dark'
                                                                ? 'bg-gray-700 border-gray-600'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                        >
                                                            <option value="1K">1K (Fast)</option>
                                                            <option value="2K">2K (Balanced)</option>
                                                            <option value="4K">4K (Highest Quality)</option>
                                                        </select>
                                                    </div>

                                                    {/* Variants per generation */}
                                                    <div>
                                                        <label className="block text-sm font-medium font-rasa mb-2">
                                                            Variants per generation
                                                        </label>
                                                        <div className="flex items-center gap-3">
                                                            <input
                                                                type="range"
                                                                min="1"
                                                                max="4"
                                                                step="1"
                                                                value={creativeStudioSettings.variantCount}
                                                                onChange={(e) => setCreativeStudioSettings({
                                                                    ...creativeStudioSettings,
                                                                    variantCount: parseInt(e.target.value, 10)
                                                                })}
                                                                className="flex-1 accent-blue-600"
                                                            />
                                                            <span className="text-sm font-rasa font-semibold w-6 text-center">
                                                                {creativeStudioSettings.variantCount}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Generate multiple options to pick from. Only the one you pick gets saved.
                                                        </p>
                                                    </div>

                                                    {/* JPRO Intelligence prompt refiner */}
                                                    <div>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={creativeStudioSettings.promptRefinerEnabled}
                                                                onChange={(e) => setCreativeStudioSettings({
                                                                    ...creativeStudioSettings,
                                                                    promptRefinerEnabled: e.target.checked
                                                                })}
                                                                className="w-4 h-4 rounded"
                                                            />
                                                            <span className="text-sm font-rasa">JPRO Intelligence prompt refiner</span>
                                                        </label>
                                                        <p className="text-xs text-gray-500 mt-1 ml-6">
                                                            Show the ✨ button to expand vague prompts before generating
                                                        </p>
                                                    </div>

                                                    {/* Thinking Process Visibility */}
                                                    <div>
                                                        <label className="flex items-center space-x-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={creativeStudioSettings.showThinkingProcess}
                                                                onChange={(e) => setCreativeStudioSettings({...creativeStudioSettings, showThinkingProcess: e.target.checked})}
                                                                className="w-4 h-4 rounded"
                                                            />
                                                            <span className="text-sm font-rasa">Show AI Thinking</span>
                                                        </label>
                                                        <p className="text-xs text-gray-500 mt-1 ml-6">See intermediate images from reasoning</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={clearStudioChat}
                                        className={`p-2 rounded-lg transition-colors ${
                                            theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                        }`}
                                        title="Clear conversation"
                                        disabled={conversationHistory.length === 0}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={clearStudioChat}
                                        className={`flex items-center space-x-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                                            theme === 'dark'
                                            ? 'border hover:bg-gray-200 text-gray-800 dark:text-gray-200 hover:dark:text-gray-800'
                                            : 'border hover:bg-gray-200 text-gray-800 dark:text-gray-200 hover:dark:text-gray-800'
                                        }`}
                                        title="Start new conversation"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span className="font-rasa font-medium">New Chat</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Creative Studio Chat Interface */}
                {activeTab === 'image' && (
                    <div
                        className="flex flex-col lg:flex-row gap-4"
                        style={{ height: 'calc(100vh - 260px)', minHeight: '520px' }}
                    >
                        <div className="flex-1 min-w-0 flex flex-col">
                        {/* Chat History — grows to fill available vertical space.
                            Inner scrollable region handles overflow. */}
                        <div
                            className={`relative mb-3 rounded-lg border flex-1 min-h-0 ${
                                isDragging
                                    ? 'border-blue-500 dark:border-blue-400 border-dashed border-4'
                                    : theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                            style={{ overflow: 'hidden' }}
                            {...dragHandlers}
                        >
                            <div className="h-full overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                                {conversationHistory.length === 0 ? (
                                    <div className={`text-center py-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <Wand2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-base font-rasa font-semibold text-gray-800 dark:text-gray-100">
                                            Creative Studio
                                        </p>
                                        <p className="text-xs font-rasa text-blue-600 dark:text-blue-400 mt-0.5">
                                            {creativeStudioSettings.resolution} · {creativeStudioSettings.aspectRatio}
                                        </p>
                                        <p className="text-xs font-rasa mt-3 max-w-xs mx-auto leading-relaxed">
                                            Describe an image, drop reference photos or docs (PDF/Word/Excel),
                                            and iterate with multi-turn edits.
                                        </p>
                                        {creativeStudioSettings.variantCount > 1 && (
                                            <p className="text-[11px] font-rasa text-blue-500 mt-2">
                                                {creativeStudioSettings.variantCount} variants per generation — pick the one you like
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    conversationHistory.map((message) => (
                                        <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} group/studio`}>
                                            <div className={`relative max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-2xl text-base rounded-lg p-3 sm:p-4 ${
                                                message.type === 'user'
                                                ? 'bg-blue-600 text-white'
                                                : message.type === 'error'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                                : message.restored
                                                ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50'
                                                : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                                            }`}>
                                                {/* Dismiss button for restored-from-history ghost messages */}
                                                {message.restored && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setConversationHistory((prev) => prev.filter((m) => m.id !== message.id));
                                                            // Also clear lineage so the next gen doesn't link to this restored row
                                                            setParentGenerationId(null);
                                                            setThoughtSignature(null);
                                                        }}
                                                        className="absolute top-1.5 right-1.5 p-0.5 rounded text-blue-600/70 hover:text-red-600 hover:bg-white/60 dark:hover:bg-gray-800/60 transition-colors"
                                                        title="Dismiss restored image"
                                                    >
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {/* Text content with proper width constraints */}
                                                <div className={`${message.images && message.images.length > 0 ? 'mb-3' : 'mb-2'}`}>
                                                    <p className="font-rasa text-sm text-base leading-relaxed break-words">{message.content}</p>
                                                </div>
                                                
                                                {/* User uploaded images */}
                                                {message.type === 'user' && message.images && (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                                        {message.images.map((img) => (
                                                            <img
                                                                key={img.id}
                                                                src={img.preview}
                                                                alt={img.name}
                                                                className="w-full h-16 sm:h-20 object-cover rounded"
                                                            />
                                                        ))}
                                                    </div>
                                                )}

                                                {/* User attached docs — chips in the bubble that used them.
                                                    Click a chip to re-attach the file to the current input pill
                                                    (so the next generation includes it again). */}
                                                {message.type === 'user' && message.attached_files && message.attached_files.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                                        {message.attached_files.map((f) => {
                                                            const alreadyAttached = (contextFiles || []).some(c => c.id === f.id || c.name === f.name);
                                                            const contentUnavailable = !f.content;  // true after chat reload — file bytes not persisted
                                                            const disabled = alreadyAttached || contentUnavailable;

                                                            const tooltip = alreadyAttached
                                                                ? `${f.name} — already attached`
                                                                : contentUnavailable
                                                                ? `${f.name} — re-upload to use again (not stored on resume)`
                                                                : `${f.name} — click to use again`;

                                                            return (
                                                                <button
                                                                    key={f.id}
                                                                    type="button"
                                                                    disabled={disabled}
                                                                    onClick={() => {
                                                                        if (!f.content) return;
                                                                        setContextFiles(prev => [...(prev || []), {
                                                                            id: crypto.randomUUID(),
                                                                            name: f.name,
                                                                            content_type: f.content_type,
                                                                            content: f.content,
                                                                        }]);
                                                                    }}
                                                                    className={`group/chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors
                                                                        ${contentUnavailable
                                                                            ? 'bg-white/5 text-white/40 border-white/10 cursor-help line-through decoration-white/30'
                                                                            : alreadyAttached
                                                                            ? 'bg-white/10 text-white/60 border-white/20 cursor-default'
                                                                            : 'bg-white/20 text-white border-white/30 hover:bg-white/30 cursor-pointer'}`}
                                                                    title={tooltip}
                                                                >
                                                                    <FileText className="w-3 h-3" />
                                                                    <span className="max-w-[160px] truncate">{f.name}</span>
                                                                    {!disabled && (
                                                                        <RefreshCw className="w-3 h-3 opacity-0 group-hover/chip:opacity-100 transition-opacity" />
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {/* Save prompt bookmark */}
                                                {message.type === 'user' && (
                                                    <div className="flex justify-end mt-1 -mb-1 opacity-0 group-hover/studio:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={() => { setSavingContent(message.content); setShowSaveDialog(true); }}
                                                            className="p-0.5 rounded text-blue-200 hover:text-white"
                                                            title="Save as prompt"
                                                        >
                                                            <Bookmark size={12} />
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Thinking process images (intermediate) */}
                                                {message.type === 'ai' && message.thinking_images && message.thinking_images.length > 0 && (
                                                    <div className="mb-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Sparkles className="w-4 h-4 text-blue-500" />
                                                            <span className="text-xs font-rasa text-gray-500">AI Thinking Process:</span>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {message.thinking_images.map((img, idx) => (
                                                                <div key={idx} className="relative">
                                                                    <img
                                                                        src={`data:${img.mime_type};base64,${img.image_base64}`}
                                                                        alt={`Thinking step ${idx + 1}`}
                                                                        className="w-full h-auto object-contain rounded border border-blue-300 opacity-70"
                                                                    />
                                                                    <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                                                                        Step {idx + 1}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* AI generated images (final) */}
                                                {message.type === 'ai' && message.images && (
                                                    <div className="grid grid-cols-1 gap-3">
                                                        {message.images.map((img, idx) => {
                                                            const isSaved = !!img._savedId;
                                                            return (
                                                                <div key={idx} className="relative text-base max-w-full sm:max-w-md">
                                                                    <img
                                                                        src={`data:${img.mime_type};base64,${img.image_base64}`}
                                                                        alt="Generated image"
                                                                        className="w-full text-base h-auto object-contain rounded border"
                                                                    />
                                                                    {/* Save to history */}
                                                                    <button
                                                                        onClick={() => handleSaveImage({
                                                                            image: img,
                                                                            promptText: img.prompt_used || message.userPromptSnapshot,
                                                                            source: { kind: 'message', messageId: message.id },
                                                                        })}
                                                                        disabled={isSaved}
                                                                        className={`absolute top-2 right-12 p-1.5 sm:p-2 rounded-full transition-colors ${
                                                                            isSaved
                                                                                ? 'bg-blue-600 text-white cursor-default'
                                                                                : 'bg-black/50 text-white hover:bg-black/70'
                                                                        }`}
                                                                        title={isSaved ? 'Saved to history' : 'Save to history'}
                                                                    >
                                                                        {isSaved
                                                                            ? <BookmarkCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                            : <Bookmark className="w-3 h-3 sm:w-4 sm:h-4" />}
                                                                    </button>
                                                                    {/* Download */}
                                                                    <button
                                                                        onClick={() => downloadImage(img)}
                                                                        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                                                                        title="Download image"
                                                                    >
                                                                        <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Grounding metadata (if available) */}
                                                {message.type === 'ai' && message.grounding_metadata && message.grounding_metadata.grounding_used && (
                                                    <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                                                        <Zap className="w-3 h-3" />
                                                        <span>Real-time data integrated</span>
                                                    </div>
                                                )}
                                                
                                                {/* Loading indicator for image generation */}
                                                {message.isLoading && (
                                                    <div className="mt-3">
                                                        <RotatingStatusIndicator inline statements={IMAGE_GEN_STATEMENTS} />
                                                    </div>
                                                )}
                                                
                                                <div className="flex justify-between items-center mt-2">
                                                    <span className={`text-xs font-rasa ${
                                                        message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                                                    }`}>
                                                        {new Date(message.timestamp).toLocaleTimeString()}
                                                    </span>
                                                    
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Drag and drop overlay */}
                            <DragDropOverlay isVisible={isDragging} size="large" />
                        </div>

                        {/* Variant picker — only rendered when generation produced N>1 variants. */}
                        {variants && variants.length > 1 && (
                            <div className={`mb-3 p-2.5 rounded-lg border ${
                                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-xs font-rasa font-semibold text-gray-700 dark:text-gray-200">
                                        Pick a variant <span className="text-gray-400 font-normal">— only the chosen one saves</span>
                                    </h4>
                                </div>
                                <VariantGrid
                                    variants={variants}
                                    pickedId={pickedVariantId}
                                    onPick={setPickedVariantId}
                                    onDownload={(v) => {
                                        const link = document.createElement('a');
                                        link.href = `data:${v.mime_type || 'image/png'};base64,${v.image_base64}`;
                                        link.download = `variant_${Date.now()}.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }}
                                />
                            </div>
                        )}

                        {/* Attached images strip — compact thumbnails. Shows above input only when attachments exist. */}
                        {uploadedImages.length > 0 && (
                            <div className="mb-2 flex gap-1.5 flex-wrap">
                                {uploadedImages.map((image) => (
                                    <div key={image.id} className="relative w-12 h-12 group">
                                        <img
                                            src={image.preview}
                                            alt={image.name}
                                            className="w-full h-full object-cover rounded border border-gray-200 dark:border-gray-700"
                                            title={image.name}
                                        />
                                        <button
                                            onClick={() => removeUploadedImage(image.id)}
                                            className="absolute -top-1 -right-1 bg-gray-700 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-80 hover:opacity-100"
                                            title="Remove"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Chat Input — unified pill. Becomes a flex-col when there are
                            context-file chips so they sit visually inside the pill, above the input row. */}
                        <div
                            className={`flex flex-col rounded-xl border transition-colors focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500/60 ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700'
                                    : 'bg-white border-gray-200'
                            }`}
                        >
                            {/* Context-file chips: inside the pill, above the input row */}
                            {contextFiles.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 px-2 pt-2">
                                    {contextFiles.map((f) => (
                                        <span
                                            key={f.id}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50"
                                            title={f.name}
                                        >
                                            <FileText className="w-3 h-3" />
                                            <span className="max-w-[140px] truncate">{f.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => setContextFiles((prev) => prev.filter((x) => x.id !== f.id))}
                                                className="ml-0.5 text-blue-500 hover:text-red-500"
                                                title="Remove"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="flex items-end gap-1">
                            {/* Attach button */}
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 ml-1 mb-1 rounded-lg transition-colors ${
                                    theme === 'dark'
                                        ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                }`}
                                title="Attach images or documents (PDF, Word, Excel)"
                                disabled={isGenerating}
                            >
                                <Plus className="w-5 h-5" />
                            </button>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                onChange={handleFileUpload}
                                className="hidden"
                            />

                            {/* Textarea — borderless; the parent provides the visual frame.
                                Auto-grows with content. `resize-y` adds the corner drag handle so
                                users can pull it taller manually. Auto-grow only INCREASES height
                                (never shrinks past a user's manual resize), so the two don't fight.
                                CSS max-height caps both at 40vh. */}
                            <textarea
                                ref={promptInputRef}
                                autoFocus
                                value={prompt}
                                onChange={(e) => {
                                    setPrompt(e.target.value);
                                    // Auto-grow only-up: respect a manual drag-resize. Compare scrollHeight
                                    // (content) against current rendered height; only grow if content needs more.
                                    const ta = e.target;
                                    const currentH = ta.clientHeight;
                                    if (ta.scrollHeight > currentH) {
                                        ta.style.height = ta.scrollHeight + 'px';
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                onPaste={handlePaste}
                                placeholder="Describe an image to create, or attach files for context…"
                                rows={1}
                                disabled={isGenerating}
                                className={`flex-1 resize-y bg-transparent border-0 outline-none focus:ring-0 py-2.5 px-1 text-sm font-rasa leading-6 ${
                                    theme === 'dark'
                                        ? 'text-white placeholder-gray-500'
                                        : 'text-gray-900 placeholder-gray-400'
                                }`}
                                style={{ minHeight: '40px', maxHeight: '40vh' }}
                            />

                            {/* Saved prompts (icon-only, subdued) */}
                            <button
                                onClick={() => setShowPromptsModal(true)}
                                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 mb-1 rounded-lg transition-colors ${
                                    theme === 'dark'
                                        ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                }`}
                                title="Saved prompts"
                                disabled={isGenerating}
                            >
                                <BookmarkCheck className="w-4 h-4" />
                            </button>

                            {/* Send */}
                            <button
                                onClick={handleStudioChat}
                                disabled={isGenerating || (!prompt.trim() && uploadedImages.length === 0)}
                                className={`flex-shrink-0 flex items-center justify-center w-10 h-10 mb-1 mr-1 rounded-lg transition-all
                                    ${(isGenerating || (!prompt.trim() && uploadedImages.length === 0))
                                        ? theme === 'dark'
                                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                    }`}
                                title={isGenerating ? 'Generating…' : 'Send'}
                            >
                                {isGenerating ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Wand2 className="w-4 h-4" />
                                )}
                            </button>
                            </div>
                        </div>

                        {/* JPRO Intelligence refiner: below input, only when enabled and there's a draft.
                            `?? true` defends against stale state where the key was added after the
                            component first mounted (dev hot-reload can preserve old shape). */}
                        {(creativeStudioSettings.promptRefinerEnabled ?? true) && prompt.trim() && (
                            <div className="mt-2 flex justify-end">
                                <PromptRefiner
                                    draft={prompt}
                                    mode="creative_studio"
                                    aspectRatio={creativeStudioSettings.aspectRatio}
                                    // Last 6 conversation turns, text only — gives the refiner enough
                                    // context to understand follow-ups like "make it bluer" without
                                    // asking redundant questions about the subject.
                                    recentMessages={conversationHistory
                                        .slice(-6)
                                        .filter(m => m.type === 'user' || m.type === 'ai')
                                        .map(m => ({
                                            role: m.type === 'user' ? 'user' : 'ai',
                                            content: m.content || '',
                                        }))
                                        .filter(m => m.content)}
                                    disabled={isGenerating}
                                    onAccept={(refined, { originalPrompt, notes }) => {
                                        setPrompt(refined);
                                        if (variants?.length) {
                                            setVariants(prev => prev.map(v => ({
                                                ...v,
                                                _originalPrompt: originalPrompt,
                                                _refinementNotes: notes,
                                            })));
                                        }
                                    }}
                                />
                            </div>
                        )}
                        </div>

                        {/* History sidebar: collapsible. Hidden entirely on mobile (<lg) to save space —
                            user can re-enable later via a button if we add one. */}
                        <div className="hidden lg:flex lg:max-h-[calc(60vh+200px)]">
                            <HistorySidebar
                                onContinueFrom={handleContinueFromHistory}
                                currentParentId={parentGenerationId}
                                collapsed={historyCollapsed}
                                onToggle={toggleHistory}
                            />
                        </div>
                    </div>
                )}


                {/* Video Generation Tab */}
                {activeTab === 'video' && (
                    <VideoGeneration />
                )}

            </div>

            {/* Info Guide Modal */}
            <InfoModal
                isOpen={showGuide}
                onClose={() => setShowGuide(false)}
                title="Image Generation Guide"
            >
                <div className="space-y-4">
                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Golden Rule
                        </h4>
                        <p className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                            <strong>Describe the scene, don't just list keywords.</strong> Write a narrative paragraph instead of disconnected words. "A cozy coffee shop at sunset with warm golden lighting streaming through large windows" works much better than "coffee shop, sunset, warm".
                        </p>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Palette className="w-4 h-4" />
                            What to Include in Prompts
                        </h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Subject:</strong> What's the main focus? Be specific</li>
                            <li><strong>Setting:</strong> Where is it? Indoor/outdoor, environment details</li>
                            <li><strong>Lighting:</strong> Golden hour, studio-lit, soft shadows, dramatic</li>
                            <li><strong>Style:</strong> Photorealistic, kawaii, minimalist, vintage, modern</li>
                            <li><strong>Camera angle:</strong> Close-up, wide shot, 45-degree angle, aerial view</li>
                            <li><strong>Colors:</strong> Warm tones, vibrant, muted, specific color palette</li>
                            <li><strong>Mood:</strong> Serene, energetic, professional, cozy, dramatic</li>
                            <li><strong>Format:</strong> Square, vertical, horizontal, wide (pick an aspect ratio in Settings)</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Wand2 className="w-4 h-4" />
                            Creative Studio Tips
                        </h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Edit conversationally:</strong> Upload an image and say "make it darker" or "add a sunset in the background"</li>
                            <li><strong>Change specific elements:</strong> "Change only the blue sofa to vintage leather, keep everything else the same"</li>
                            <li><strong>Style transfer:</strong> "Transform this photo into watercolor painting style"</li>
                            <li><strong>Iterate:</strong> Refine over multiple messages - each response builds on the previous</li>
                            <li><strong>Upload multiple images:</strong> "Combine the person from image 1 with the background from image 2"</li>
                        </ul>
                    </section>

                    <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-blue-600" />
                            Advanced Features
                        </h4>

                        <div className="space-y-3">
                            {/* Resolution Control */}
                            <div>
                                <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                    <Zap className="w-4 h-4" />
                                    Resolution Control (1K/2K/4K)
                                </h5>
                                <p className="text-sm ml-6">Choose output quality in Settings:</p>
                                <ul className="text-sm ml-8 space-y-1">
                                    <li>• <strong>1K:</strong> Fast generation, good for drafts</li>
                                    <li>• <strong>2K:</strong> Balanced quality and speed (default)</li>
                                    <li>• <strong>4K:</strong> Maximum quality for professional work</li>
                                </ul>
                            </div>

                            {/* Real-Time Data */}
                            <div>
                                <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-green-600" />
                                    Real-Time Data (Automatic)
                                </h5>
                                <p className="text-sm ml-6">AI automatically searches for current information when needed:</p>
                                <ul className="text-sm ml-8 space-y-1">
                                    <li>• <strong>Weather:</strong> "Show today's weather in Tokyo"</li>
                                    <li>• <strong>Stocks:</strong> "Create a chart of current stock prices"</li>
                                    <li>• <strong>Events:</strong> "Visualize the latest news event"</li>
                                    <li>• <strong>Current data:</strong> "Show the current moon phase"</li>
                                </ul>
                                <p className="text-xs mt-2 ml-6 italic text-gray-600 dark:text-gray-400">
                                    Always enabled - AI detects when real-time data is needed
                                </p>
                            </div>

                            {/* Multi-Image References */}
                            <div>
                                <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                    <Boxes className="w-4 h-4 text-orange-600" />
                                    Multi-Image References (Up to 14)
                                </h5>
                                <p className="text-sm ml-6">Upload reference images for visual consistency:</p>
                                <ul className="text-sm ml-8 space-y-1">
                                    <li>• <strong>Objects:</strong> Up to 6 product/object references</li>
                                    <li>• <strong>People:</strong> Up to 5 human references</li>
                                    <li>• <strong>Total:</strong> Maximum 14 reference images</li>
                                </ul>
                                <div className="text-sm ml-6 mt-2 space-y-1">
                                    <p><strong>Example uses:</strong></p>
                                    <p className="italic">Upload 3 product photos: "Show these items arranged on a desk"</p>
                                    <p className="italic">Upload person's photo: "Generate them in different outfits"</p>
                                    <p className="italic">Upload style references: "Create new images matching this aesthetic"</p>
                                </div>
                            </div>

                            {/* Thinking Process Visibility */}
                            <div>
                                <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                    <Lightbulb className="w-4 h-4 text-purple-600" />
                                    AI Thinking Process
                                </h5>
                                <p className="text-sm ml-6">Enable "Show AI Thinking" to see intermediate images:</p>
                                <ul className="text-sm ml-8 space-y-1">
                                    <li>• <strong>Transparency:</strong> See how AI refines compositions</li>
                                    <li>• <strong>Intermediate steps:</strong> View 2-3 thinking images</li>
                                    <li>• <strong>Learning tool:</strong> Understand AI's creative process</li>
                                    <li>• <strong>Final image:</strong> Always provided after thinking steps</li>
                                </ul>
                                <p className="text-xs mt-2 ml-6 italic text-gray-600 dark:text-gray-400">
                                    AI uses reasoning to improve compositions before final output
                                </p>
                            </div>

                            {/* High-Fidelity Text */}
                            <div>
                                <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                    <Edit3 className="w-4 h-4 text-blue-600" />
                                    High-Fidelity Text Rendering (Always On)
                                </h5>
                                <p className="text-sm ml-6">Professional typography for:</p>
                                <ul className="text-sm ml-8 space-y-1">
                                    <li>• <strong>Logos:</strong> Professional brand designs</li>
                                    <li>• <strong>Posters:</strong> Legible marketing materials</li>
                                    <li>• <strong>Signs:</strong> Clear text on images</li>
                                    <li>• <strong>Typography:</strong> Stylized text art</li>
                                </ul>
                                <p className="text-xs mt-2 ml-6 italic text-gray-600 dark:text-gray-400">
                                    Always enabled for best quality
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                        <h4 className="font-semibold text-base mb-3 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-green-600" />
                            Practical Examples
                        </h4>

                        <div className="space-y-3">
                            <div className="border-l-4 border-blue-500 pl-3">
                                <p className="font-semibold text-sm">Real-Time Weather</p>
                                <p className="text-sm italic mt-1">
                                    "Generate a beautiful image showing the current weather in Paris"
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    → AI automatically searches for current conditions and generates accurate visualization
                                </p>
                            </div>

                            <div className="border-l-4 border-orange-500 pl-3">
                                <p className="font-semibold text-sm">Product Consistency</p>
                                <p className="text-sm mt-1">
                                    <strong>Action:</strong> Upload 3 product photos as references
                                </p>
                                <p className="text-sm italic mt-1">
                                    "Show these three products arranged on a modern desk"
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    → AI maintains exact visual consistency in new composition
                                </p>
                            </div>

                            <div className="border-l-4 border-purple-500 pl-3">
                                <p className="font-semibold text-sm">Understanding AI Process</p>
                                <p className="text-sm mt-1">
                                    <strong>Settings:</strong> Enable "Show AI Thinking"
                                </p>
                                <p className="text-sm italic mt-1">
                                    "Create a complex futuristic cityscape with flying cars"
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    → See 2-3 intermediate images showing AI refining the composition
                                </p>
                            </div>

                            <div className="border-l-4 border-blue-500 pl-3">
                                <p className="font-semibold text-sm">Professional Logo</p>
                                <p className="text-sm italic mt-1">
                                    "Create a modern tech logo with the text 'CloudSync' in bold font"
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    → Crisp, legible text with professional typography (always enabled)
                                </p>
                            </div>

                            <div className="border-l-4 border-green-500 pl-3">
                                <p className="font-semibold text-sm">Combining Features</p>
                                <p className="text-sm mt-1">
                                    <strong>Upload:</strong> 2 product photos as references
                                </p>
                                <p className="text-sm italic mt-1">
                                    "Create a poster showing these products with current weather in New York, include text 'Summer Sale'"
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    → All features work together: real-time data + product consistency + high-quality text
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Iterate Instead of Restarting
                        </h4>
                        <div className="space-y-2">
                            <p>Every message builds on the last one. Instead of rewriting a prompt from scratch, say what to change: warmer light, swap the sofa for leather, make it a 16:9 banner.</p>
                            <p>Turn up <strong>Variants per generation</strong> in Settings to see several options at once. Only the variant you pick gets saved to history.</p>
                        </div>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Film className="w-4 h-4" />
                            Need Video?
                        </h4>
                        <p>Switch to the <strong>Video</strong> tab for AI-powered video generation with script assistance.</p>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Edit3 className="w-4 h-4" />
                            Prompt Templates
                        </h4>
                        <div className="space-y-4">
                            <div>
                                <p className="font-medium mb-1">Photorealistic Scenes</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        A photorealistic [shot type] of [subject], [action or expression], set in [environment]. The scene is illuminated by [lighting description], creating a [mood] atmosphere. Captured with a [camera/lens details], emphasizing [key textures and details]. [Aspect ratio] format.
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "A photorealistic close-up portrait of an elderly Japanese ceramicist with deep wrinkles, inspecting a tea bowl in his rustic workshop. Soft golden hour light streaming through a window. Captured with 85mm portrait lens. Vertical format."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Stickers & Icons</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        A [style] sticker of a [subject], featuring [key characteristics] and a [color palette]. The design should have [line style] and [shading style]. The background must be transparent.
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "A kawaii-style sticker of a happy red panda wearing a tiny bamboo hat. Bold clean outlines, simple cel-shading, vibrant colors. Transparent background."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Product Photography</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        A high-resolution, studio-lit product photograph of a [product description] on a [background surface]. The lighting is a [lighting setup] to [lighting purpose]. The camera angle is a [angle type] to showcase [specific feature]. Ultra-realistic, with sharp focus on [key detail]. [Aspect ratio].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "A high-resolution, studio-lit product photograph of a minimalist ceramic coffee mug in matte black on polished concrete. Three-point softbox lighting. 45-degree elevated angle. Sharp focus on steam rising. Square format."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Logos & Text</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        Create a [image type] for [brand/concept] with the text "[text to render]" in a [font style]. The design should be [style description], with a [color scheme].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Create a modern, minimalist logo for a coffee shop called 'The Daily Grind' in a bold sans-serif font. Clean design with warm brown and cream colors."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Minimalist & Negative Space</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        A minimalist composition featuring a single [subject] positioned in the [bottom-right/top-left/etc.] of the frame. The background is a vast, empty [color] canvas, creating significant negative space. Soft, subtle lighting. [Aspect ratio].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "A minimalist composition featuring a single, delicate red maple leaf positioned in the bottom-right of the frame. The background is a vast, empty off-white canvas, creating significant negative space. Soft, diffused lighting from the top left. Square image."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Comic Panels & Sequential Art</p>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mb-1 border border-purple-200 dark:border-purple-800">
                                    <p className="text-sm font-mono">
                                        A single comic book panel in a [art style] style. In the foreground, [character description and action]. In the background, [setting details]. The panel has a [dialogue/caption box] with the text "[Text]". The lighting creates a [mood] mood. [Aspect ratio].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "A single comic book panel in a gritty, noir art style with high-contrast black and white inks. In the foreground, a detective in a trench coat stands under a flickering streetlamp, rain soaking his shoulders. In the background, the neon sign of a desolate bar reflects in a puddle. A caption box at the top reads 'The city was a tough place to keep secrets.' Dramatic, somber mood. Landscape."
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Image Editing Templates (Creative Studio)
                        </h4>
                        <div className="space-y-4">
                            <div>
                                <p className="font-medium mb-1">Adding/Removing Elements</p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mb-1 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm font-mono">
                                        Using the provided image of [subject], please [add/remove/modify] [element] to/from the scene. Ensure the change is [description of how the change should integrate].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Using the provided image of my cat, please add a small, knitted wizard hat on its head. Make it look like it's sitting comfortably and matches the soft lighting of the photo."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Inpainting (Change Specific Parts)</p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mb-1 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm font-mono">
                                        Using the provided image, change only the [specific element] to [new element/description]. Keep everything else in the image exactly the same, preserving the original style, lighting, and composition.
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Using the provided image of a living room, change only the blue sofa to be a vintage, brown leather chesterfield sofa. Keep the rest of the room, including the pillows on the sofa and the lighting, unchanged."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Style Transfer</p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mb-1 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm font-mono">
                                        Transform the provided photograph of [subject] into the artistic style of [artist/art style]. Preserve the original composition but render it with [description of stylistic elements].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Transform the provided photograph of a modern city street at night into the artistic style of Vincent van Gogh's 'Starry Night'. Preserve the original composition of buildings and cars, but render all elements with swirling, impasto brushstrokes and a dramatic palette of deep blues and bright yellows."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Combining Multiple Images</p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mb-1 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm font-mono">
                                        Create a new image by combining the elements from the provided images. Take the [element from image 1] and place it with/on the [element from image 2]. The final image should be a [description of the final scene].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Create a professional e-commerce fashion photo. Take the blue floral dress from the first image and let the woman from the second image wear it. Generate a realistic, full-body shot of the woman wearing the dress, with the lighting and shadows adjusted to match the outdoor environment."
                                </p>
                            </div>

                            <div>
                                <p className="font-medium mb-1">Detail Preservation</p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mb-1 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm font-mono">
                                        Using the provided images, place [element from image 2] onto [element from image 1]. Ensure that the features of [element from image 1] remain completely unchanged. The added element should [description of how the element should integrate].
                                    </p>
                                </div>
                                <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                    Example: "Take the first image of the woman with brown hair, blue eyes, and a neutral expression. Add the logo from the second image onto her black t-shirt. Ensure the woman's face and features remain completely unchanged. The logo should look like it's naturally printed on the fabric, following the folds of the shirt."
                                </p>
                            </div>
                        </div>
                    </section>

                    

                </div>
            </InfoModal>

            <SavedPromptsModal
                isOpen={showPromptsModal}
                onClose={() => setShowPromptsModal(false)}
                onUse={(content) => setPrompt(content)}
                currentFeature="studio"
            />
            <SavePromptDialog
                isOpen={showSaveDialog}
                onClose={() => setShowSaveDialog(false)}
                content={savingContent}
                featureTag="studio"
            />
        </div>
    );
}

export default Imagen;
                